import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, KeyRound, Loader2, Sparkles } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import type { KeysOut, StatusOut } from '../lib/api'
import { cn } from '../lib/cn'
import { MultiCsvUpload } from '../components/MultiCsvUpload'

type StepKey = 'keys' | 'upload' | 'enrich' | 'index' | 'done'

interface JobOut {
  id: string
  kind: string
  status: 'pending' | 'running' | 'done' | 'failed'
  message: string | null
  progress_current: number
  progress_total: number
  result?: { enriched?: number; missing?: number; failed?: number; embedded?: number; fts?: number } | null
  error?: string | null
}

function StepHeader({ index, title, state }: { index: number; title: string; state: 'todo' | 'active' | 'done' }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium',
          state === 'done' && 'bg-green-500/20 text-green-300 border border-green-500/40',
          state === 'active' && 'bg-accent text-white',
          state === 'todo' && 'bg-muted text-gray-500 border border-border',
        )}
      >
        {state === 'done' ? <Check size={14} /> : index}
      </div>
      <h2 className={cn('text-base font-medium', state === 'todo' ? 'text-gray-500' : 'text-gray-100')}>
        {title}
      </h2>
    </div>
  )
}

export function Onboarding() {
  const nav = useNavigate()
  const qc = useQueryClient()

  const keysQ = useQuery<KeysOut>({
    queryKey: ['keys'],
    queryFn: async () => (await api.get<KeysOut>('/keys')).data,
  })
  const statusQ = useQuery<StatusOut>({
    queryKey: ['status'],
    queryFn: async () => (await api.get<StatusOut>('/status')).data,
    refetchInterval: 5000,
  })

  const allKeysSet =
    !!keysQ.data && keysQ.data.apify_token_set && keysQ.data.openai_set &&
    keysQ.data.cohere_set && keysQ.data.anthropic_set

  const hasConnections = !!statusQ.data && statusQ.data.total_connections > 0
  const enriched = statusQ.data?.enriched ?? 0
  const total = statusQ.data?.total_connections ?? 0
  const pending = statusQ.data?.pending ?? -1  // -1 sentinel: still loading status
  // Done = pending count is small. Some pending profiles will always remain
  // (Apify caps, transient timeouts, profiles that need a paid retry).
  // Tolerate up to max(50, 2% of total) so the UI advances even on partial
  // enrichment, while still blocking when most of the network is unprocessed.
  const tolerance = Math.max(50, Math.round(total * 0.02))
  const enrichmentDone = total > 0 && pending >= 0 && pending <= tolerance
  const indexedDone = (statusQ.data?.embedded ?? 0) >= (statusQ.data?.enriched ?? 0) && enrichmentDone

  // Determine current step from server state
  const currentStep: StepKey = !allKeysSet
    ? 'keys'
    : !hasConnections
    ? 'upload'
    : !enrichmentDone
    ? 'enrich'
    : !indexedDone
    ? 'index'
    : 'done'

  return (
    <div className="min-h-screen flex items-start justify-center px-6 py-10">
      <div className="w-full max-w-2xl space-y-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Setup</div>
          <h1 className="text-2xl font-semibold text-gray-100 mt-1">Get your network ready</h1>
          <p className="text-sm text-gray-500 mt-1">
            One-time. After this you'll be able to rank against your full LinkedIn network with natural-language queries.
          </p>
        </div>

        <div className="card p-6 space-y-6">
          <StepHeader
            index={1}
            title="Add your API keys"
            state={currentStep === 'keys' ? 'active' : allKeysSet ? 'done' : 'todo'}
          />
          {currentStep === 'keys' && (
            <KeysStep onDone={() => qc.invalidateQueries({ queryKey: ['keys'] })} />
          )}
          {allKeysSet && currentStep !== 'keys' && (
            <div className="text-sm text-gray-500 ml-10 -mt-3">All four keys saved.</div>
          )}
        </div>

        <div className="card p-6 space-y-6">
          <StepHeader
            index={2}
            title="Upload your LinkedIn Connections CSV"
            state={currentStep === 'upload' ? 'active' : hasConnections ? 'done' : 'todo'}
          />
          {currentStep === 'upload' && (
            <UploadStep
              onDone={() => qc.invalidateQueries({ queryKey: ['status'] })}
            />
          )}
          {hasConnections && currentStep !== 'upload' && (
            <div className="text-sm text-gray-500 ml-10 -mt-3">
              {total.toLocaleString()} connections seeded.
            </div>
          )}
        </div>

        <div className="card p-6 space-y-6">
          <StepHeader
            index={3}
            title="Enrich profiles via Apify"
            state={
              currentStep === 'enrich'
                ? 'active'
                : enrichmentDone
                ? 'done'
                : 'todo'
            }
          />
          {currentStep === 'enrich' && (
            <EnrichStep
              total={total}
              enriched={enriched}
              onDone={() => qc.invalidateQueries({ queryKey: ['status'] })}
            />
          )}
          {enrichmentDone && currentStep !== 'enrich' && (
            <div className="text-sm text-gray-500 ml-10 -mt-3">{enriched} enriched.</div>
          )}
        </div>

        <div className="card p-6 space-y-6">
          <StepHeader
            index={4}
            title="Build search index"
            state={
              currentStep === 'index'
                ? 'active'
                : indexedDone
                ? 'done'
                : 'todo'
            }
          />
          {currentStep === 'index' && (
            <IndexStep
              onDone={() => qc.invalidateQueries({ queryKey: ['status'] })}
            />
          )}
          {indexedDone && (
            <div className="ml-10 -mt-3 space-y-3">
              <div className="text-sm text-gray-500">Index built. You're ready.</div>
              <button className="btn-primary" onClick={() => nav('/', { replace: true })}>
                <Sparkles size={14} /> Open the search page
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function KeysStep({ onDone }: { onDone: () => void }) {
  const [apify, setApify] = useState('')
  const [openai, setOpenai] = useState('')
  const [cohere, setCohere] = useState('')
  const [anthropic, setAnthropic] = useState('')

  const save = useMutation({
    mutationFn: async () => {
      await api.put('/keys', {
        apify_token: apify || undefined,
        openai_api_key: openai || undefined,
        cohere_api_key: cohere || undefined,
        anthropic_api_key: anthropic || undefined,
      })
    },
    onSuccess: onDone,
  })

  const allSet = apify && openai && cohere && anthropic

  return (
    <div className="ml-10 space-y-3">
      <p className="text-sm text-gray-400">
        BYOK — pasted keys are encrypted at rest. The platform charges nothing.
      </p>
      <KeyField label="Apify token" value={apify} onChange={setApify} placeholder="apify_api_…" />
      <KeyField label="OpenAI API key" value={openai} onChange={setOpenai} placeholder="sk-…" />
      <KeyField label="Cohere API key" value={cohere} onChange={setCohere} />
      <KeyField label="Anthropic API key" value={anthropic} onChange={setAnthropic} placeholder="sk-ant-…" />
      <div className="flex items-center justify-between pt-2">
        <a
          href="https://console.apify.com/account/integrations"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-gray-500 hover:text-gray-300"
        >
          Where do I get these?
        </a>
        <button
          className="btn-primary"
          disabled={!allSet || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Saving…
            </>
          ) : (
            <>
              <KeyRound size={14} /> Save keys
            </>
          )}
        </button>
      </div>
      {save.isError && (
        <div className="text-sm text-red-400">{apiErrorMessage(save.error)}</div>
      )}
    </div>
  )
}

function KeyField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <div className="space-y-1.5">
      <label className="label">{label}</label>
      <input
        type="password"
        className="input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
    </div>
  )
}

function UploadStep({ onDone }: { onDone: () => void }) {
  return (
    <div className="ml-10">
      <MultiCsvUpload
        helpText={
          <>
            Export from LinkedIn at{' '}
            <span className="font-mono text-gray-300">
              Settings → Data privacy → Get a copy of your data → Connections only
            </span>
            . Drop multiple CSVs if you have splits — duplicates are skipped automatically.
          </>
        }
        onComplete={onDone}
      />
    </div>
  )
}

function EnrichStep({
  total,
  enriched,
  onDone,
}: {
  total: number
  enriched: number
  onDone: () => void
}) {
  const cost = useMemo(() => (total * 0.005).toFixed(2), [total])
  const [jobId, setJobId] = useState<string | null>(null)

  // On mount, ask backend if there's already a running enrichment job for this user
  // (handles page refresh — the actual job lives server-side in auth.db).
  const existingJobsQ = useQuery<JobOut[]>({
    queryKey: ['jobs', 'enrich_profile'],
    queryFn: async () => (await api.get<JobOut[]>('/jobs')).data,
    enabled: !jobId,
    staleTime: 0,
  })
  useEffect(() => {
    if (jobId || !existingJobsQ.data) return
    const live = existingJobsQ.data.find(
      (j) => j.kind === 'enrich_profile' && (j.status === 'running' || j.status === 'pending'),
    )
    if (live) setJobId(live.id)
  }, [existingJobsQ.data, jobId])

  const start = useMutation({
    mutationFn: async () => {
      const res = await api.post<JobOut>('/enrich/profile')
      return res.data
    },
    onSuccess: (data) => setJobId(data.id),
  })

  const jobQ = useQuery<JobOut>({
    queryKey: ['job', jobId],
    queryFn: async () => (await api.get<JobOut>(`/jobs/${jobId}`)).data,
    enabled: !!jobId,
    refetchInterval: 5000,
  })

  // When the job finishes, invalidate status so the parent re-checks.
  useEffect(() => {
    if (jobQ.data?.status === 'done') onDone()
  }, [jobQ.data?.status, onDone])

  // Still discovering whether a job is running — show a loader instead of the start button
  // so the user can't accidentally trigger a duplicate.
  if (!jobId && existingJobsQ.isLoading) {
    return (
      <div className="ml-10 text-sm text-gray-400 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Checking job status…
      </div>
    )
  }

  if (!jobId) {
    return (
      <div className="ml-10 space-y-3">
        <p className="text-sm text-gray-400">
          Apify will fetch full profile data (about, experiences, education, certifications, languages) for{' '}
          <span className="text-gray-200">{total.toLocaleString()}</span> connections. Cost is <span className="text-gray-200">~${cost}</span>.
          This is the only step that costs money on your Apify bill.
        </p>
        <p className="text-sm text-gray-500">
          Wall time: ~30–90 minutes depending on Apify queue. You can leave this tab.
        </p>
        <div className="flex items-center justify-end pt-1">
          <button className="btn-primary" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Starting…
              </>
            ) : (
              <>
                <Sparkles size={14} /> Start enrichment
              </>
            )}
          </button>
        </div>
        {start.isError && (
          <div className="text-sm text-red-400">{apiErrorMessage(start.error)}</div>
        )}
      </div>
    )
  }

  const pct = total > 0 ? Math.min(100, Math.round((enriched / total) * 100)) : 0
  return (
    <div className="ml-10 space-y-2">
      <div className="text-sm text-gray-300">
        {jobQ.data?.status === 'failed'
          ? <span className="text-red-400">Failed: {jobQ.data?.error}</span>
          : <span>{jobQ.data?.message ?? 'Starting…'}</span>}
      </div>
      <div className="h-2 bg-muted rounded overflow-hidden">
        <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-gray-500">
        {enriched.toLocaleString()} of {total.toLocaleString()} enriched · {pct}%
      </div>
    </div>
  )
}

function IndexStep({ onDone }: { onDone: () => void }) {
  const [jobId, setJobId] = useState<string | null>(null)

  // Adopt any in-flight index_rebuild job before we auto-kick a new one (refresh-safe).
  const existingJobsQ = useQuery<JobOut[]>({
    queryKey: ['jobs', 'index_rebuild'],
    queryFn: async () => (await api.get<JobOut[]>('/jobs')).data,
    enabled: !jobId,
    staleTime: 0,
  })

  const start = useMutation({
    mutationFn: async () => (await api.post<JobOut>('/enrich/index')).data,
    onSuccess: (data) => setJobId(data.id),
  })

  const jobQ = useQuery<JobOut>({
    queryKey: ['job', jobId],
    queryFn: async () => (await api.get<JobOut>(`/jobs/${jobId}`)).data,
    enabled: !!jobId,
    refetchInterval: 3000,
  })

  useEffect(() => {
    if (jobQ.data?.status === 'done') onDone()
  }, [jobQ.data?.status, onDone])

  // Auto-kick the index job — but only after we know no live one is already running.
  useEffect(() => {
    if (jobId || start.isPending || existingJobsQ.isLoading) return
    const live = existingJobsQ.data?.find(
      (j) => j.kind === 'index_rebuild' && (j.status === 'running' || j.status === 'pending'),
    )
    if (live) {
      setJobId(live.id)
    } else {
      start.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingJobsQ.data, existingJobsQ.isLoading])

  return (
    <div className="ml-10 text-sm text-gray-300 space-y-1.5">
      {jobQ.data?.status === 'failed' ? (
        <span className="text-red-400">Failed: {jobQ.data.error}</span>
      ) : (
        <span>{jobQ.data?.message ?? 'Computing embeddings…'}</span>
      )}
      <div className="h-1.5 bg-muted rounded overflow-hidden">
        <div className="h-full bg-accent animate-pulse" style={{ width: '60%' }} />
      </div>
    </div>
  )
}
